import type { Translations } from './it'

export const fr: Translations = {
  common: {
    ctaLabel: "Discutons ensemble",
    freeNote: "15 minutes de consultation gratuite, sans engagement.",
  },
  header: {
    backToTop: "Retour en haut",
    logoAlt: "Fra un viaggio e l’altro — Francesca Zaccari, travel designer",
    logoAltShort: "Fra un viaggio e l’altro",
    mainNavAria: "Navigation principale",
    mobileNavAria: "Navigation mobile",
    openMenu: "Ouvrir le menu",
    closeMenu: "Fermer le menu",
    languageLabel: "Changer de langue",
    nav: {
      about: "Qui je suis",
      services: "Services",
      itineraries: "Itinéraires",
      how: "Comment ça marche",
      reviews: "Avis",
    },
  },
  hero: {
    eyebrow: "Travel Designer · Accompagnatrice touristique",
    greetingBefore: "Bonjour, je m’appelle ",
    greetingAccent: "Francesca",
    titleBefore: "Votre voyage",
    titleAccent: "à travers l’art.",
    paragraph:
      "Je m’appelle Francesca, je suis Travel Designer et je construis des itinéraires sur mesure qui lisent un lieu à travers son art et son histoire. Pas de voyages « préfabriqués », seulement des voyages qui vous laissent quelque chose.",
    paragraphMobile:
      "Je suis Travel Designer et je construis des itinéraires sur mesure qui lisent un lieu à travers son art et son histoire. Pas de voyages « préfabriqués », seulement des voyages qui vous laissent quelque chose.",
    colosseumAlt: "Le Colisée à Rome au coucher du soleil",
    positanoAlt: "Les maisons colorées de Positano face à la mer",
    polaroidCaption: "Côte amalfitaine",
    badge: "voyages d’art dans le monde",
    girlAlt: "Illustration d’une voyageuse à l’écharpe orange tenant un livre d’art",
  },
  about: {
    eyebrow: "Qui je suis",
    titleBefore: "Bonjour, je suis Francesca,",
    titleAccent: "historienne de l’art en voyage.",
    p1: "J’ai étudié à l’Université de Bologne, où j’ai obtenu deux diplômes : patrimoine culturel et arts visuels. En somme, j’ai passé des années à apprendre à regarder les villes comme on regarde un tableau — et je ne me suis jamais arrêtée.",
    p2: "Après mes études, j’ai vécu un an au Canada, puis je suis rentrée suivre la formation de Travel Designer à Milan : c’est là que j’ai appris le métier de construire des itinéraires sur mesure, étape par étape. À Ravenne, j’ai obtenu ma licence d’accompagnatrice touristique.",
    p3Before:
      "Aujourd’hui, je travaille comme accompagnatrice sur des food tours pour plusieurs agences et j’accompagne des groupes à l’étranger.",
    p3Brand: "Fra un viaggio e l’altro",
    p3After:
      "est mon projet personnel : jeune et soigné dans les moindres détails, tout comme les itinéraires que je dessine sur mesure.",
    portraitAlt: "Francesca Zaccari, travel designer et accompagnatrice touristique",
    portraitMissing: "photo bientôt disponible",
    mapAlt: "Carte de voyage avec des notes et des itinéraires tracés à la main",
    mapCaption: "notes de route",
    badgeAria: "Travel Designer certifiée",
    steps: [
      { place: "Bologna", text: "Deux diplômes en patrimoine culturel et arts visuels" },
      { place: "Canada", text: "Un an de vie à l’étranger après le diplôme" },
      { place: "Milano", text: "Formation de Travel Designer : itinéraires sur mesure" },
      { place: "Ravenna", text: "Licence d’accompagnatrice touristique" },
    ],
  },
  concept: {
    eyebrow: "Le concept",
    titleBefore: "Pas un package.",
    titleAccent: "Un point de vue.",
    paragraph:
      "Je relie voyage, art et culture : chaque itinéraire raconte un lieu à travers une clé artistique ou historique. Pas une liste de choses à voir, mais une histoire à traverser — au bon rythme, avec du contexte et quelques détours qui valent le voyage.",
    points: [
      {
        title: "Itinéraires culturels et artistiques",
        text: "Chaque voyage a sa clé de lecture : un artiste, une époque, un mouvement.",
      },
      {
        title: "Pour voyageurs internationaux et groupes",
        text: "Je conçois et j’accompagne des parcours pour celles et ceux qui viennent de loin et qui voyagent ensemble.",
      },
      {
        title: "Une méthode d’historienne de l’art",
        text: "Sources, contextes, détails : le même soin qu’on met à étudier une œuvre.",
      },
    ],
    badge: "exemple d’itinéraire",
    cardTitle: "Sur les traces du Caravage",
    cardText:
      "Un itinéraire entre Rome et Naples pour explorer les deux villes avec les yeux du peintre : les toiles dans les églises, les rues qu’il a arpentées, la lumière qui a changé la peinture.",
    cardMeta: "Rome → Naples · art · histoire",
    galleryAlt: "Intérieur d’une galerie d’art italienne avec des tableaux aux murs",
    cinqueTerreAlt: "Les villages colorés des Cinque Terre face à la mer",
    discoverCta: "Découvrir plus",
  },
  services: {
    eyebrow: "Services",
    titleBefore: "Ce que je peux faire",
    titleAccent: "pour vous",
    items: [
      {
        title: "Travel Design",
        description:
          "Un itinéraire de voyage entièrement personnalisé, construit sur mesure selon qui vous êtes, ce qui vous passionne et votre façon de voyager. Pas de voyages tout faits : j’étudie étapes, rythmes et expériences rien que pour vous. Les réservations passent ensuite par une agence, vous n’avez à penser à rien.",
      },
      {
        title: "Accompagnement touristique",
        description:
          "En plus de dessiner l’itinéraire, je peux accompagner physiquement vous ou votre groupe, en Italie et à l’étranger. Musées, villes d’art, chemins hors des sentiers battus, culture gastronomique : je voyage avec vous et je veille à ce que la logistique du voyage fonctionne.",
      },
      {
        title: "Relecture d’itinéraire",
        description:
          "Vous avez déjà un plan de voyage ? Je l’examine avec un œil d’experte et vous dis franchement ce qui fonctionne, ce qui ne va pas et ce qui manque : étapes trop serrées, horaires qui ne collent pas, pépites que vous risquez de manquer à deux pas de votre route.",
      },
      {
        title: "Guides de voyage",
        description:
          "Contenus et ressources de voyage à lire entre deux voyages : guides, conseils pratiques et regards d’art pour arriver préparé et voir chaque lieu autrement.",
      },
    ],
    bottomText: "Vous ne savez pas par où commencer ?",
    discoverCta: "Découvrir les services",
  },
  servicesPage: {
    eyebrow: "Les services en détail",
    titleBefore: "Quatre façons de voyager",
    titleAccent: "avec moi.",
    intro:
      "Chaque voyage est différent, et chaque voyageur a besoin de quelque chose de différent : certains veulent un itinéraire construit de zéro, d’autres une guide à leurs côtés, d’autres encore un simple second avis. Voici en détail ce que je peux faire pour vous — avec un exemple concret pour chaque service.",
    exampleLabel: "Un exemple concret",
    idealForLabel: "Pour qui",
    items: [
      {
        example:
          "Une semaine à Paris sur les traces des Impressionnistes : le musée d’Orsay tôt le matin, loin de la foule ; une promenade à Montmartre, là où ces peintres vivaient et travaillaient ; une journée à Giverny, dans les jardins de Monet. Un rythme détendu, avec le temps pour les cafés historiques et les librairies de la Rive Gauche.",
        idealFor: "Pour celles et ceux qui veulent un voyage construit sur mesure, sans penser à rien.",
      },
      {
        example:
          "Trois jours à Florence avec un groupe d’amis : les Offices racontés avec calme, salle après salle ; l’Oltrarno des artisans, loin des sentiers battus ; un coucher de soleil depuis un point panoramique absent des guides. Je voyage avec vous et vous raconte ce que nous voyons, en historienne de l’art.",
        idealFor: "Pour les groupes, les familles et les voyageurs qui veulent une guide experte à leurs côtés.",
      },
      {
        example:
          "Vous aviez prévu Rome en deux jours avec huit étapes par jour ? Je le recalibre avec vous : je supprime les trajets inutiles, je réorganise les visites par quartier et j’ajoute cette église avec deux Caravages que vous risquiez de manquer à deux pas de votre hôtel.",
        idealFor: "Pour celles et ceux qui ont déjà un plan de voyage mais veulent un œil expert avant le départ.",
      },
      {
        example:
          "Un guide numérique sur mesure avant le départ : quoi voir et dans quel ordre, comment se déplacer, où manger et les clés de lecture artistique de chaque étape — à consulter hors ligne, directement sur le téléphone.",
        idealFor: "Pour les voyageurs indépendants qui veulent partir préparés.",
      },
    ],
    ctaTitle: "Vous ne savez pas lequel choisir ?",
    ctaSubtitle:
      "Racontez-moi tout lors d’une consultation gratuite de 15 minutes : nous trouverons ensemble par où commencer.",
    backHome: "Retour à l’accueil",
  },
  itinerariesPage: {
    eyebrow: "Itinéraires de voyage artistiques et culturels",
    titleBefore: "Voyages avec",
    titleAccent: "un point de vue.",
    intro: "Voici quelques exemples d’itinéraires que je peux construire et personnaliser avec vous.",
    detailsLabel: "L’expérience",
    durationLabel: "Durée indicative",
    backHome: "Retour à l'accueil",
    closeModalAria: "Fermer les détails de l’itinéraire",
    itineraryLabel: "Itinéraire",
    discoverMore: "Découvrir plus",
    ctaEyebrow: "Construisons votre voyage",
    ctaTitle: "Vous avez déjà une destination en tête ?",
    ctaText: "Partons de vos envies et transformons-les en itinéraire sur mesure.",
    items: [
      { title: "Le Caravage entre Rome et Naples", description: "Églises, places et chefs-d’œuvre racontés à travers la lumière et l’histoire du peintre.", details: "Un parcours qui part de Rome et nous montre les débuts du peintre entre les Musées Capitolins, les églises et la Galerie Borghèse, pour arriver ensuite à Naples, où le Caravage se sent enfin compris.", duration: "5 jours", cover: "/images/caravaggio-giuditta.jpg", coverAlt: "Judith tranchant la tête d’Holopherne, Le Caravage" },
      { title: "Impressionnistes en Provence", description: "De Cézanne à Van Gogh, la Provence et ses champs de lavande en fleur vus à travers leurs yeux.", details: "Un itinéraire entre les villes qui ont inspiré les grands maîtres de l’impressionnisme, pour lire un paysage qui a changé la façon de voir l’art.", duration: "5–7 jours", cover: "/uploads/eabb27de4d.jpg", coverAlt: "Champ de blé avec vue sur Saint-Rémy, Van Gogh" },
      { title: "Sur les traces de l’abbaye de Pomposa", description: "Trois itinéraires d’un jour, un seul fil conducteur : l’abbaye de Pomposa, phare de foi et de culture au Moyen Âge entre le Pô et la mer.", details: "1 · Giotto entre Padoue et Pomposa\nDe Padoue, entre la Chapelle des Scrovegni et la Basilique Saint-Antoine, jusqu’à Pomposa : sur les traces des influences du peintre le long de la côte romagnole.\n\n2 · La mosaïque entre Ravenne et Pomposa\nOn part des mosaïques byzantines et paléochrétiennes de Ravenne et de Sant’Apollinare in Classe, pour arriver à Pomposa devant le pavement mosaique médiéval : un parcours qui referme un cercle ouvert des siècles plus tôt.\n\n3 · Dante, l’exil et les derniers jours\nDe Ravenne, dans les lieux qui l’ont accueillie pendant la rédaction de la Comédie, jusqu’à Pomposa, où il connut son enfer personnel : là il tombe malade et peu après il meurt.", duration: "3 itinéraires · 1 jour chacun", cover: "/uploads/252221080f.jpg", coverAlt: "Intérieur de la basilique avec fresques médiévales" },
      { title: "Florence à la Renaissance", description: "La ville des Médicis à travers artistes, ateliers et architecture.", details: "Les Offices, l’Oltrarno, les chapelles et ateliers d’artisans à un rythme équilibré, avec les bonnes pauses.", duration: "2–4 jours", cover: "/uploads/e89d5c2b97.jpg", coverAlt: "Panorama de Florence avec le Duomo" },
      { title: "Venise entre art et eau", description: "Une Venise à parcourir avec curiosité, entre chefs-d’œuvre et détails cachés.", details: "San Marco, le Dorsoduro, les îles de la lagune et des parcours alternatifs pour découvrir la ville sans se presser.", duration: "3 jours", cover: "/images/venezia.jpg", coverAlt: "Un canal de Venise" },
      { title: "Les Cinque Terre au-delà de la carte postale", description: "Villages, sentiers et paysages ligures à un rythme lent et attentif.", details: "Un itinéraire entre mer, culture locale et petits producteurs, loin des étapes les plus fréquentées.", duration: "3–5 jours", cover: "/images/cinque-terre.jpg", coverAlt: "Les villages colorés des Cinque Terre" },
    ],
  },
  collaborations: {
    navLabel: "Collaborations",
    eyebrow: "Collaborations",
    titleBefore: "Les structures qui",
    titleAccent: "voyagent avec moi.",
    intro:
      "En plus des voyages sur mesure, je travaille avec des tour-opérateurs et des structures locales qui partagent ma façon de voyager. Ici, vous trouvez tous les circuits et groupes avec lesquels je pars, réservables directement auprès d’eux.",
    backHome: "Retour à l’accueil",
    homeEyebrow: "Collaborations",
    homeTitleBefore: "Je voyage aussi",
    homeTitleAccent: "en compagnie.",
    homeText:
      "Food tours en Émilie-Romagne, circuits en groupe en Italie et dans le monde, coordinatrice de voyage : certains de mes départs sont réservables directement auprès des structures avec lesquelles je collabore.",
    homeCta: "Découvrir les collaborations",
    foodTours: {
      title: "Food tours",
      role: "Accompagnatrice touristique",
      intro:
        "La nourriture, c’est la culture : j’accompagne des food tours en Émilie-Romagne, entre marchés historiques, boutiques et tables dressées. Chaque circuit est réservable directement auprès de l’organisation.",
      partners: [
        {
          place: "Rimini · avec Hili",
          text: "À Rimini, je déambule avec Hili entre les étapes du centre historique : poisson frais, piadina et histoires de la ville, dans un circuit qui mêle goût et curiosité.",
          code: "",
          codeLabel: "",
          links: [
            { label: "Get Your Guide", url: "https://gyg.me/GOCSOAtk" },
            { label: "Viator", url: "https://www.viator.com/it-IT/tours/Rimini/Rimini-Walking-Food-Tour-and-Highlights/d30027-326991P39" },
          ],
        },
        {
          place: "Bologne · avec Do Eat Better",
          text: "À Bologne, je guide le circuit gastronomique de Do Eat Better Experience : les saveurs de la ville racontées entre les étals du marché et les boutiques du centre. Avec mon code, vous bénéficiez de 5% de remise.",
          code: "FRANCIZ5BOLOGNA",
          codeLabel: "Code promo 5% : ",
          links: [
            { label: "Do Eat Better Experience", url: "https://doeatbetterexperience.com/italy-food-tour/what-to-do-in-bologna/bologna-food-tour/" },
          ],
        },
        {
          place: "Bologne · avec Tours and The City",
          text: "Avec Tours and The City, je guide la Bologna Gastronomic Experience : marchés historiques, boutiques et recettes de tradition racontées à table.",
          code: "",
          codeLabel: "",
          links: [
            { label: "Tours and The City", url: "https://toursandthecity.com/bologna-gastronomic-experience-with-a-local/" },
          ],
        },
        {
          place: "Bologne · avec Taste Bologna",
          text: "Avec Taste Bologna, j’accompagne le Classic Bologna Food Tour : osterias historiques, marchés et boutiques familiales, entre parmesan, mortadelle et tortellini faits main, en petits groupes.",
          code: "",
          codeLabel: "",
          links: [
            { label: "Taste Bologna", url: "https://www.tastebologna.net/classic-bologna" },
          ],
        },
      ],
    },
    village: {
      title: "The Village",
      role: "Accompagnatrice touristique",
      text: "Pour The Village, j’accompagne les circuits en Émilie-Romagne, avec des départs de Bologne et Ravenne : des voyages courts et soignés à la découverte des merveilles de la région.",
      linkLabel: "The Village Italy",
      url: "https://www.thevillageitaly.com/",
    },
    realWanderlust: {
      title: "Real Wanderlust",
      role: "Coordinatrice de voyages en groupe",
      text: "J’accompagne les voyages en groupe de Real Wanderlust partout dans le monde, entre itinéraires soignés et esprit libre.",
      linkLabel: "Real Wanderlust",
      url: "https://www.realwanderlust.it/",
    },
    theVoyage: {
      title: "The Voyage",
      role: "Experte locale de Bologne",
      text: "Avec The Voyage, je fais découvrir Bologne aux voyageurs du monde entier : des tours entre les portiques, le marché et les histoires de la ville la plus étroite d’Italie.",
      linkLabel: "Mon profil sur The Voyage",
      url: "https://www.thevoyage.co/locals/francesca-zaccari",
    },
    aurea: {
      title: "Aurea Viaggi",
      role: "Accompagnatrice touristique",
      text: "Avec Aurea Viaggi, j\u2019accompagne des voyages en groupe au rythme lent : des destinations d\u2019art et de nature savour\u00e9es sans pr\u00e9cipitation, avec des temps confortables et des r\u00e9cits soign\u00e9s, pour ceux qui pr\u00e9f\u00e8rent go\u00fbter chaque \u00e9tape.",
      linkLabel: "Aurea Viaggi",
      url: "https://aureaviaggicastenaso.my.canva.site/",
      tour: {
        eyebrow: "Prochain tour",
        title: "La cascade des Marmore, sur le th\u00e8me de l\u2019eau",
        text: "Du 9 au 11 avril 2027, nous explorons ensemble la cascade des Marmore, c\u00e9l\u00e9br\u00e9e par les po\u00e8tes \u00e0 travers l\u2019eau : terre, art et nature.",
        email: "fra.unviaggioelaltro@gmail.com",
        emailSubject: "Tour cascade des Marmore \u2014 Aurea Viaggi, 9-11 avril",
        emailLabel: "\u00c9crivez-moi pour plus d\u2019infos",
      },
    },
    weroad: {
      title: "WeRoad",
      role: "Coordinatrice de voyage de groupe",
      text: "Sur WeRoad, je pars comme coordinatrice de voyage : des groupes de personnes du même âge, des itinéraires pensés pour vivre la destination ensemble et une pointe d’aventure.",
      linkLabel: "Prochains départs — profil coordinatrice",
      url: "https://www.weroad.it/coordinatori/francesca-zaccari",
    },
  },
  process: {
    eyebrow: "Le parcours",
    titleBefore: "Comment ça",
    titleAccent: "marche",
    steps: [
      {
        title: "On fait connaissance",
        description:
          "Quinze minutes de consultation gratuite : vous me racontez où vous voulez aller, ce que vous aimez et comment vous voyagez. Sans engagement, juste pour comprendre si je peux vous être utile.",
      },
      {
        title: "Je dessine votre voyage",
        description:
          "Je construis l’itinéraire sur mesure : étapes, expériences, rythmes. Aucun copier-coller, chaque voyage part de zéro et de la personne qui le vivra.",
      },
      {
        title: "C’est parti",
        description:
          "L’itinéraire est prêt. Les réservations passent par une agence, vous n’avez plus qu’à faire vos valises et partir.",
      },
    ],
  },
  reviews: {
    eyebrow: "Avis",
    titleBefore: "Les mots de celles et ceux qui ont voyagé",
    titleAccent: "avec moi",
    prevAria: "Avis précédent",
    nextAria: "Avis suivant",
    goToAria: "Aller à l’avis {n}",
    items: [
      {
        name: "Noemi G.",
        initial: "N",
        trip: "Budapest, 3 jours sur mesure",
        text: "Francesca a su répondre à mes besoins. Après avoir choisi ensemble les attractions et lieux emblématiques de Budapest à visiter, elle a créé un itinéraire sur mesure de 3 jours. Les quelques ajustements faits en cours de route (comme inverser certaines visites entre le deuxième et le troisième jour) n’ont pas gâché l’expérience. Au contraire, ils m’ont permis de suivre mon rythme et de profiter pleinement de la capitale.",
      },
      {
        name: "Giulia Z.",
        initial: "G",
        trip: "Tour d’Irlande en préparation",
        text: "Je vis à Dublin depuis 11 mois déjà, mais je n’avais jamais eu l’occasion de mieux découvrir l’Irlande. Je me suis donc tournée vers Francesca, qui organise un tour selon mes besoins et mes préférences. Elle me tient informée de chaque changement et nouveauté. Elle a compris exactement le type d’expérience que je veux vivre et planifie tout avec beaucoup de passion et d’engagement. À ce stade, j’ai hâte de découvrir tous les endroits que Francesca aura dénichés au cœur des paysages fascinants de l’Irlande.",
      },
      {
        name: "Soana G.",
        initial: "S",
        trip: "Devis détaillé",
        text: "Je dois féliciter Francesca, car son devis était très détaillé et soigné dans les moindres détails... Vraiment douée, je la recommande à tous ceux qui rêvent d'un voyage, car elle fait tout pour réaliser votre rêve et faire du voyage une expérience unique.",
      },
      {
        name: "Luca C.",
        initial: "L",
        trip: "Vietnam et Philippines, relecture d'itinéraire",
        text: "J'ai fait la consultation gratuite avec Francesca puis j'ai décidé de faire relire mon itinéraire. Bien que mon voyage au Vietnam et aux Philippines ait été organisé très à la dernière minute, avec déplacements et hôtels réservés quelques jours avant le départ, elle a réussi à réorganiser l'itinéraire au mieux et nous avons pu tout voir comme nous le voulions, de façon optimale. Sa consultation a été indispensable pour les correspondances des vols et à partir de là nous avons compris comment nous déplacer.",
      },
      {
        name: "Marica D.",
        initial: "M",
        trip: "Bologne, tour de ville avec accompagnement",
        text: "J’avais déjà rencontré Francesca lors d’un voyage de groupe en août et, après cette première expérience, j’ai eu le plaisir de la retrouver comme accompagnatrice à la découverte de Bologne, pendant que j’y étais pour le travail. Ce fut une très belle expérience : Francesca m’a guidée parmi les principaux points d’intérêt de la ville, en me racontant ses curiosités avec passion et compétence. La visite a été agréable et jamais banale, grâce aussi à son mode très disponible et à sa capacité de mettre à l’aise. Naturellement, la partie gastronomique n’a pas manqué : nous avons goûté plusieurs spécialités de la cuisine bolognaise, qui ont rendu la journée encore plus complète et authentique. Ce fut un vrai plaisir de passer du temps avec Francesca et de découvrir Bologne à travers ses récits et son point de vue. Une personne préparée, solaire et passionnée, que je recommande absolument à quiconque veut connaître Bologne non seulement comme touriste, mais aussi à travers ses histoires, ses traditions et sa cuisine.",
      },
    ],
  },
  ctaBand: {
    title: "Un voyage en tête ?",
    subtitle: "Racontez-le-moi lors d’une consultation gratuite de 15 minutes.",
  },
  finalCta: {
    quote:
      "[…] Sous le bleu profond du ciel, un oiseau de mer s’en va ; il ne s’arrête jamais : car toutes les images portent écrit : « plus loin ».",
    titleBefore: "Prête à partir ?",
    titleAccent: "Votre voyage commence ici.",
    subtext: "Aucun engagement d'achat.",
    polaroidCaption: "Nouveaux points de vue",
    positanoAlt: "Un panorama à couper le souffle découvert en voyage",
    veneziaAlt: "Un canal de Venise au coucher du soleil",
  },
  privacy: {
    backLink: "Retour à l’accueil",
    seoTitle: "Politique de confidentialité — Fra un viaggio e l’altro",
    title:
      "INFORMATION SUR LE TRAITEMENT DES DONNÉES PERSONNELLES CONFORMÉMENT À L’ARTICLE 13 DU RÈGLEMENT UE 2016/679",
    intro:
      "La présente politique de confidentialité décrit la manière dont Fra un viaggio e l’altro collecte, utilise, protège et partage les données personnelles des utilisateurs qui visitent le site www.fraunviaggioelaltro.it, dans le plein respect du Règlement UE 2016/679 (RGPD) et de la législation italienne en matière de protection des données personnelles.",
    sections: [
      {
        heading: "Responsable du traitement",
        paragraphs: ["Le responsable du traitement des données est :"],
        bullets: ["Fra un viaggio e l’altro", "Francesca Zaccari", "fra.unviaggioelaltro@gmail.com"],
        outro: "",
      },
      {
        heading: "Types de données collectées",
        paragraphs: [
          "Lors de la navigation et/ou de l’utilisation des services proposés par le site, nous pouvons collecter les catégories de données suivantes :",
        ],
        bullets: [
          "Données d’identification : nom, prénom, e-mail, numéro de téléphone (ex. formulaire de contact ou demande d’informations).",
          "Données de navigation : adresse IP, type de navigateur, données relatives à la session, pages visitées (via des cookies techniques ou analytiques).",
          "Données fournies volontairement : messages, commentaires, avis, réservations, inscription à la newsletter.",
        ],
        outro: "",
      },
      {
        heading: "Finalités du traitement",
        paragraphs: [
          "Les données personnelles collectées sont traitées aux fins suivantes :",
        ],
        bullets: [
          "Répondre aux demandes envoyées via le formulaire de contact ;",
          "Fournir des informations ou des devis sur les voyages et services touristiques ;",
          "Inscrire l’utilisateur à la newsletter (avec consentement préalable) ;",
          "Améliorer l’expérience utilisateur et les fonctionnalités du site ;",
          "Respecter des obligations légales ou fiscales.",
        ],
        outro: "",
      },
      {
        heading: "Base juridique du traitement",
        paragraphs: ["Le traitement des données repose sur :"],
        bullets: [
          "Le consentement exprès de la personne concernée (ex. inscription à la newsletter) ;",
          "L’exécution de mesures précontractuelles ou contractuelles (ex. demande de devis ou d’informations) ;",
          "Les obligations légales auxquelles le responsable du traitement est soumis ;",
          "L’intérêt légitime du responsable du traitement, dans le cas d’analyses statistiques anonymes destinées à améliorer les services.",
        ],
        outro: "",
      },
      {
        heading: "Modalités du traitement",
        paragraphs: [
          "Le traitement des données s’effectue de manière licite, loyale et transparente, au moyen d’outils manuels et informatiques. Des mesures techniques et organisationnelles appropriées sont adoptées pour garantir la sécurité et la confidentialité des données, en évitant tout accès non autorisé, perte ou divulgation.",
        ],
        bullets: [],
        outro: "",
      },
      {
        heading: "Communication et diffusion des données",
        paragraphs: [
          "Les données personnelles ne seront jamais diffusées à des tiers, sauf dans les cas suivants :",
        ],
        bullets: [
          "Sujets fournissant des services pour le compte du responsable du traitement (ex. hébergement web, gestion de la newsletter, consultants informatiques) ;",
          "Obligations légales, autorités judiciaires ou administrations publiques ;",
          "Collaborateurs ou partenaires étroitement liés à la gestion des services proposés (ex. agences de voyage ou tour-opérateurs).",
        ],
        outro:
          "Tous les sujets externes qui traitent les données pour le compte du responsable du traitement sont nommés Responsables du traitement et garantissent des mesures de protection adéquates.",
      },
      {
        heading: "Conservation des données",
        paragraphs: [
          "Les données seront conservées pendant la durée strictement nécessaire à la réalisation des finalités indiquées, sauf obligations légales différentes. En particulier :",
        ],
        bullets: [
          "Données de contact : jusqu’au traitement de la demande ou au retrait du consentement ;",
          "Données pour la newsletter : jusqu’au désabonnement ;",
          "Données de navigation : selon les délais définis dans la Politique relative aux cookies.",
        ],
        outro: "",
      },
      {
        heading: "Droits de la personne concernée",
        paragraphs: [
          "Conformément aux articles 15-22 du RGPD, l’utilisateur peut exercer à tout moment les droits suivants :",
        ],
        bullets: [
          "Accès à ses données personnelles ;",
          "Rectification ou mise à jour ;",
          "Effacement (« droit à l’oubli ») ;",
          "Limitation du traitement ;",
          "Opposition au traitement ;",
          "Portabilité des données ;",
          "Retrait du consentement (sans préjudice de la licéité du traitement antérieur) ;",
          "Réclamation auprès de l’Autorité Garante italienne pour la Protection des Données Personnelles.",
        ],
        outro:
          "Pour exercer vos droits, vous pouvez envoyer une demande à l’adresse e-mail fra.unviaggioelaltro@gmail.com.",
      },
      {
        heading: "Transfert de données hors de l’UE",
        paragraphs: [
          "En règle générale, les données ne sont pas transférées hors de l’Espace Économique Européen. En cas de nécessité, le transfert aura lieu dans le respect des garanties prévues par le RGPD (ex. décisions d’adéquation, clauses contractuelles types).",
        ],
        bullets: [],
        outro: "",
      },
      {
        heading: "Politique relative aux cookies",
        paragraphs: [
          "Ce site utilise des cookies techniques et, avec votre consentement préalable, également des cookies de profilage ou analytiques. Pour plus d’informations et pour gérer vos préférences, veuillez consulter la Politique relative aux cookies dédiée.",
        ],
        bullets: [],
        outro: "",
      },
      {
        heading: "Modifications de la présente information",
        paragraphs: [
          "Fra un viaggio e l’altro se réserve le droit de modifier à tout moment la présente politique de confidentialité, en publiant la version mise à jour sur le site. Les utilisateurs sont invités à consulter régulièrement cette page.",
        ],
        bullets: [],
        outro: "",
      },
    ],
  },
  notFound: {
    title: "Page introuvable",
    message: "La page {path} n’existe pas.",
    goHome: "Retour à l’accueil",
    goBack: "Retour en arrière",
  },
  footer: {
    privacyLink: "Politique de confidentialité",
    logoAlt: "Fra un viaggio e l’altro — logo",
    copyright:
      "© {year} Fra un viaggio e l’altro · Francesca Zaccari — Travel Designer & Accompagnatrice touristique · TVA 02803860390",
  },
}
